import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Spinner, Alert, Card, Badge, Button, InputGroup, Form } from 'react-bootstrap';
import { 
  ChevronRight, ChevronDown, BookOpen, Layers, Star, 
  Info, Search, Maximize2, Minimize2, ArrowLeft 
} from 'lucide-react';

import api from '../../api/api';
import { getCurrentUser } from '../../utils/auth';
import AppFooter from '../Common/AppFooter'; 

const ConceptGraphViewer = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [nodes, setNodes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const user = getCurrentUser();
  const slug = user?.tenant_slug;

  useEffect(() => {
    const fetchGraph = async () => {
      try {
        if (!slug) {
          setError("Tenant session not found.");
          setLoading(false);
          return;
        }
        const res = await api.get(`/tenant/${slug}/projects/${projectId}/graph-snapshot`);
        setNodes(res.data || []);
      } catch (err) {
        setError("Failed to load concept graph.");
      } finally {
        setLoading(false);
      }
    };
    fetchGraph();
  }, [projectId, slug]);

  const filteredNodes = useMemo(() => {
    if (!searchTerm) return nodes;
    return nodes.filter(node => 
      node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      node.id.toString().includes(searchTerm)
    );
  }, [nodes, searchTerm]);

  const toggleNode = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const setAllExpanded = (state) => {
    const newState = {};
    nodes.forEach(node => newState[node.id] = state);
    setExpanded(newState);
  };

  const getBloomColor = (level) => {
    const colors = { 1: '#10b981', 2: '#3b82f6', 3: '#f59e0b', 4: '#ef4444', 5: '#8b5cf6', 6: '#ec4899' };
    return colors[level] || '#64748b';
  };

  const renderTree = (parentId = null, level = 0) => {
    const children = searchTerm 
      ? (level === 0 ? filteredNodes : []) 
      : nodes.filter(n => n.parent_id === parentId);

    if (children.length === 0) return null;

    return (
      <div className={level > 0 && !searchTerm ? "ms-4 border-start ps-4" : ""}>
        {children.map(node => {
          const isExpanded = expanded[node.id];
          const hasChildren = nodes.some(n => n.parent_id === node.id);

          return (
            <div key={node.id} className="mb-3">
              <div 
                className={`d-flex align-items-center p-3 rounded-3 shadow-sm border ${node.is_core ? 'border-primary' : 'border-light'}`}
                style={{ 
                  backgroundColor: 'white',
                  borderLeft: `5px solid ${node.is_core ? '#0d6efd' : '#dee2e6'}`,
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                {!searchTerm && hasChildren && (
                  <Button variant="link" className="p-0 me-2 text-dark" onClick={() => toggleNode(node.id)}>
                    {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </Button>
                )}

                <div className="flex-grow-1">
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span className="fw-bold text-dark">{node.name}</span>
                    {node.is_core && <Badge bg="primary" style={{ fontSize: '0.65rem' }}>CORE</Badge>}
                  </div>
                  
                  <div className="d-flex gap-3 small text-muted">
                    <span className="d-flex align-items-center gap-1">
                      <Layers size={14} style={{ color: getBloomColor(node.bloom) }} />
                      Bloom: {node.bloom}
                    </span>
                    <span className="d-flex align-items-center gap-1 text-truncate">
                      <Star size={14} className="text-warning" />
                      Diff: {node.difficulty}/10
                    </span>
                  </div>
                </div>
              </div>

              {!searchTerm && isExpanded && renderTree(node.id, level + 1)}
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) return <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light"><Spinner animation="grow" variant="primary" aria-hidden="true" /></div>;

  return (
    <div className="d-flex flex-column min-vh-100 bg-light">
      
      {/* 
        Note: Assuming <WhiteLabelHeader /> is rendered in your global Router/App Layout. 
        If it needs to be explicitly rendered on this page, just drop it right here!
      */}

      <Container className="py-4 flex-grow-1" style={{ maxWidth: '1000px' }}>
        
        {/* Navigation Action */}
        <div className="mb-3">
          <Button 
            variant="white" 
            className="d-inline-flex align-items-center gap-2 text-muted fw-bold border shadow-sm rounded-pill px-3 py-2 bg-white"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={16} /> Back to Projects
          </Button>
        </div>

        {error && <Alert variant="danger" role="alert">{error}</Alert>}
        
        <Card className="border-0 shadow-lg" style={{ borderRadius: '15px', overflow: 'hidden' }}>
          <div className="bg-primary p-4 text-white">
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <h4 className="mb-1 fw-bold d-flex align-items-center gap-2"><BookOpen /> Concept Knowledge Base</h4>
                <p className="mb-0 opacity-75 small">Concept Mapping as per the Selected Content Standard</p>
              </div>
              <Badge bg="light" className="text-primary p-2 fs-6 shadow-sm">{nodes.length} Total Concepts</Badge>
            </div>

            <div className="d-flex gap-2">
              <InputGroup className="shadow-sm">
                <InputGroup.Text className="bg-white border-0"><Search size={18} className="text-muted" /></InputGroup.Text>
                <Form.Control 
                  placeholder="Search concepts by name or ID..." 
                  className="border-0 shadow-none"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              {!searchTerm && (
                <>
                  <Button variant="light" className="shadow-sm" onClick={() => setAllExpanded(true)} title="Expand All"><Maximize2 size={18}/></Button>
                  <Button variant="light" className="shadow-sm" onClick={() => setAllExpanded(false)} title="Collapse All"><Minimize2 size={18}/></Button>
                </>
              )}
            </div>
          </div>
          
          <Card.Body className="bg-light p-4" style={{ minHeight: '60vh', maxHeight: '70vh', overflowY: 'auto' }}>
            {filteredNodes.length > 0 ? (
              renderTree(null)
            ) : (
              <div className="text-center py-5 text-muted d-flex flex-column align-items-center justify-content-center h-100">
                <Info size={48} className="mb-3 opacity-25" />
                <h5>No matching concepts found</h5>
                <p className="small">Try adjusting your search terms.</p>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>

      <div className="mt-auto">
        <AppFooter />
      </div>
      
    </div>
  );
};

export default ConceptGraphViewer;